import type { CompetenceRecord } from "../../api/client";
import { DateField } from "../../components/DateField";
import { TextField } from "../../components/TextField";

/**
 * Screen 15 — audit experience and training.
 *
 * The four figures are `type="text"` with `inputmode="numeric"`. `type="number"` returns
 * null from `selectionStart`, so the caret cannot be restored on a controlled field, and a
 * scroll wheel over one silently changes a value a certification body produces to its
 * accreditation body. Data dictionary §9.
 *
 * The witnessed audit is Cl. 7.2.9 and is deliberately not required. Screen 15 says it in
 * as many words: an auditor this body has not yet witnessed is an open item on the record,
 * not a blocker — and the server answers the same way, with an advisory.
 */
export function ExperienceStep({
  record,
  set,
  onAdd,
  onSet,
  onRemove,
  errorFor,
}: {
  record: CompetenceRecord;
  set: (patch: Partial<CompetenceRecord>) => void;
  onAdd: () => void;
  onSet: (index: number, patch: Record<string, string>) => void;
  onRemove: (index: number) => void;
  errorFor: (field: string) => string | undefined;
}) {
  const training = record.training ?? [];

  return (
    <div className="card">
      <h2>Audit experience</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        Accreditation bodies check minimum audit experience against IAF mandatory documents.
        These figures form part of the record your certification body must be able to produce
        on assessment.
      </p>

      <div className="grid2">
        <TextField
          id="experience-ms-years"
          label="Years in management systems"
          kind="numeric"
          value={record.ms_years ?? ""}
          onChange={(ms_years) => set({ ms_years })}
          error={errorFor("ms_years")}
          required
        />
        <TextField
          id="experience-sector-years"
          label="Years in the technical sector"
          kind="numeric"
          value={record.sector_years ?? ""}
          onChange={(sector_years) => set({ sector_years })}
          error={errorFor("sector_years")}
          required
        />
      </div>
      <div className="grid2">
        <TextField
          id="experience-audits-led"
          label="Audits led"
          kind="numeric"
          value={record.audits_led ?? ""}
          onChange={(audits_led) => set({ audits_led })}
          error={errorFor("audits_led")}
          required
        />
        <TextField
          id="experience-audit-days"
          label="Total audit days"
          kind="numeric"
          value={record.audit_days ?? ""}
          onChange={(audit_days) => set({ audit_days })}
          error={errorFor("audit_days")}
          required
        />
      </div>

      <div className="grid2">
        <DateField
          id="experience-witnessed"
          label="Most recent witnessed audit"
          hint="Cl. 7.2.9 — ongoing performance monitoring. Leave blank if this certification body has not yet witnessed you."
          value={record.witnessed_on ?? ""}
          onChange={(witnessed_on) => set({ witnessed_on })}
          error={errorFor("witnessed_on")}
        />
        <TextField
          id="experience-witnessed-by"
          label="Witnessed by"
          placeholder="Who observed it"
          value={record.witnessed_by ?? ""}
          onChange={(witnessed_by) => set({ witnessed_by })}
          error={errorFor("witnessed_by")}
        />
      </div>

      <div className="hr" />
      <div className="between">
        <div className="eyebrow">Training records</div>
        <button type="button" className="btn sec sm" onClick={onAdd}>
          Add training
        </button>
      </div>

      {training.map((entry, index) => (
        <div key={index} className="plan-ed" style={{ marginTop: "10px" }}>
          <div className="grid3">
            <TextField
              id={`training-${index}-course`}
              label="Course"
              value={entry.course ?? ""}
              onChange={(course) => onSet(index, { course })}
              error={errorFor(`training.${index}.course`)}
            />
            <TextField
              id={`training-${index}-provider`}
              label="Provider"
              value={entry.provider ?? ""}
              onChange={(provider) => onSet(index, { provider })}
              error={errorFor(`training.${index}.provider`)}
            />
            <DateField
              id={`training-${index}-date`}
              label="Date"
              value={entry.completed_on ?? ""}
              onChange={(completed_on) => onSet(index, { completed_on })}
              error={errorFor(`training.${index}.completed_on`)}
            />
          </div>
          {errorFor(`training.${index}`) ? (
            <p className="err" role="alert">
              {errorFor(`training.${index}`)}
            </p>
          ) : null}
          <div style={{ marginTop: "9px" }}>
            <button type="button" className="btn danger sm" onClick={() => onRemove(index)}>
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
